/** A/S 수량비중 그래프(세부사항) 그룹·하위 항목 설정 */

export const AS_REPAIR_QTY_DETAIL_CATEGORIES = {
    totalQty: ['전체수량'],
    shortage: ['수량부족'],
    return: ['폐기제품', '수리기간초과', '불량명다름'],
    paid: [
        '정상수량',
        '스크레치',
        '이물질',
        'LCD파손',
        'Micom파손',
        '다이얼파손',
        '온도센서파손',
        '커넥터파손',
        '케이블파손',
        '미세먼지소음',
    ],
    free: [
        '전원불량',
        '터치불량',
        '소리불량',
        '홈넷불량',
        '보일러불량',
        '에어컨불량',
        '서브PCB불량',
        'Micom교체',
        'LCD고장',
        '릴레이교체',
        '통신불량',
        '세팅불량',
        '커넥터교체',
        '작동정상'
    ],
    done: ['수리완료수량'],
};

/** 합계 필드를 라벨로 자동 탐색하는 기본 그룹 */
export const AS_REPAIR_QTY_CHART_BUILTIN_GROUP_IDS = ['shortage', 'return', 'paid', 'free'];

const DEFAULT_GROUPS = [
    { id: 'shortage', title: '수량부족', color: '#f97316' },
    { id: 'return', title: '반품수량', color: '#ef4444' },
    { id: 'paid', title: '유상수리수량', color: '#8b5cf6' },
    { id: 'free', title: '무상수리수량', color: '#14b8a6' },
];

const COLOR_PATTERN = /^#[0-9a-f]{6}$/i;

export const buildDefaultAsRepairQtyChartConfig = () => ({
    groups: DEFAULT_GROUPS.map((g) => ({
        ...g,
        visible: true,
        fieldKeys: [],
        categories: (AS_REPAIR_QTY_DETAIL_CATEGORIES[g.id] || []).map((title) => ({
            title,
            fieldKeys: [],
            children: [],
        })),
    })),
});

const normalizeFieldKeys = (raw) => {
    const list = Array.isArray(raw) ? raw : [];
    const seen = new Set();
    const out = [];
    list.forEach((k) => {
        const key = String(k ?? '').trim();
        if (key && !seen.has(key)) {
            seen.add(key);
            out.push(key);
        }
    });
    return out;
};

/** 하위 항목 목록 — 하위 항목은 한 단계(children)까지만 허용 */
const normalizeCategoryList = (raw, allowChildren) => (
    (Array.isArray(raw) ? raw : [])
        .map((c) => {
            const title = String((typeof c === 'string' ? c : c?.title) ?? '').trim();
            if (!title) {
                return null;
            }
            const item = {
                title,
                fieldKeys: normalizeFieldKeys(typeof c === 'string' ? [] : c?.fieldKeys),
            };
            if (allowChildren) {
                item.children = normalizeCategoryList(typeof c === 'string' ? [] : c?.children, false);
            }
            return item;
        })
        .filter(Boolean)
);

export const normalizeAsRepairQtyChartConfig = (raw) => {
    const groupsRaw = Array.isArray(raw?.groups) ? raw.groups : null;
    if (!groupsRaw || groupsRaw.length === 0) {
        return buildDefaultAsRepairQtyChartConfig();
    }
    const usedIds = new Set();
    const groups = [];
    groupsRaw.forEach((g, index) => {
        if (!g || typeof g !== 'object') {
            return;
        }
        let id = String(g.id ?? '').trim() || `group_${index + 1}`;
        while (usedIds.has(id)) {
            id = `${id}_${index + 1}`;
        }
        usedIds.add(id);
        const fallback = DEFAULT_GROUPS.find((d) => d.id === id);
        const title = String(g.title ?? '').trim() || fallback?.title || `그룹${index + 1}`;
        const color = COLOR_PATTERN.test(String(g.color || '')) ? String(g.color) : (fallback?.color || '#64748b');
        const categories = normalizeCategoryList(g.categories, true);
        groups.push({
            id,
            title,
            color,
            visible: g.visible !== false,
            fieldKeys: normalizeFieldKeys(g.fieldKeys),
            categories,
        });
    });
    return groups.length > 0 ? { groups } : buildDefaultAsRepairQtyChartConfig();
};

export const getAsRepairQtyChartGroup = (chartConfig, groupId) => (
    normalizeAsRepairQtyChartConfig(chartConfig).groups.find((g) => g.id === groupId) || null
);
